import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { MsalHelperService } from '../services/msal-helper.service';
import { Router } from '@angular/router';
import { StorageRouterService } from '../services/storage-router.service';
import { Settings } from '../flows/settings';
import { ApplicationInsightsService } from '../services/application-insights.service';
import { FlowEventService } from '../services/flow-event.service';
import { AccountInfo } from '@azure/msal-browser';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  public error: any;
  @Input() user$ = null;
  tokenFlow = null;
  tokenAdmin = null;
  countdown: number | null = null;
  redirecting = false;
  cachedUser: AccountInfo | null = null;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private router: Router,
    private msalHelperService: MsalHelperService,
    private storageDexieService: StorageRouterService,
    private appInsights: ApplicationInsightsService,
    private flowEventService: FlowEventService
  ) {
    this.error = this.msalHelperService.getError();
  }

  get allConnected(): boolean {
    return !!this.tokenFlow;
  }

  get userAccessConnected(): boolean {
    return !!(this.tokenFlow || this.cachedUser);
  }

  async ngOnInit() {
    this.user$ = this.msalHelperService.user$;
    this.cachedUser = this.msalHelperService.getUser();
    this.tokenFlow = this.msalHelperService.getCachedTokenInternal(['https://service.flow.microsoft.com//Flows.Manage.All']);
    this.tokenAdmin = this.msalHelperService.getCachedTokenInternal(['https://service.flow.microsoft.com//User']);

    if (this.allConnected) {
      this.startCountdown();
      return;
    }

    const ok = await this.msalHelperService.trySilentPowerTokens();
    if (ok) {
      this.tokenFlow = this.msalHelperService.getCachedTokenInternal(['https://service.flow.microsoft.com//Flows.Manage.All']);
      this.tokenAdmin = this.msalHelperService.getCachedTokenInternal(['https://service.flow.microsoft.com//User']);
      this.cachedUser = this.msalHelperService.getUser();
      if (this.allConnected) {
        this.startCountdown();
        return;
      }
    }
  }

  ngOnDestroy() {
    this.clearCountdown();
  }

  private startCountdown(seconds = 5): void {
    this.countdown = seconds;
    this.countdownTimer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearCountdown();
        this.redirecting = true;
        this.navigateAway();
      }
    }, 1000);
  }

  cancelCountdown(): void {
    this.clearCountdown();
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdown = null;
  }

  private navigateAway(): void {
    this.storageDexieService.getConfig(Settings.SETTING_LASTROUTE).then(setting => {
      let route = setting?.value || '/flows';
      if (route === '/' || route.startsWith('/settings')) {
        route = '/flows';
      }
      this.router.navigateByUrl(route);
    });
  }

  navigate(url: string) {
    this.router.navigateByUrl(url);
  }

  displayUser(user: AccountInfo | any): string {
    return user?.username || user?.name || 'your account';
  }

  login($event) {
    $event.stopPropagation();
    this.appInsights.trackEvent(`app`, { "select": "login" });
    this.msalHelperService.login();
  }

  loginM365($event) {
    $event.stopPropagation();
    this.appInsights.trackEvent(`app`, { "select": "login-m365" });
    this.msalHelperService.loginM365Consent();
  }

  loginAdmin($event) {
    $event.stopPropagation();
    this.appInsights.trackEvent(`app`, { "select": "login" });
    this.msalHelperService.loginPowerAdmin();
  }

  loginDefault($event) {
    $event.stopPropagation();
    this.appInsights.trackEvent(`app`, { "select": "login" });
    this.msalHelperService.loginDefault();
  }

  welcomeBack($event) {
    $event.stopPropagation();
    this.clearCountdown();
    this.redirecting = true;
    this.navigateAway();
  }

  openBugReport() {
    this.flowEventService.showFeedback$.emit();
  }

}
